const uploadFileUrl = process.env.REACT_APP_FILE_SERVICES_URL;

/**
 * Uploads a single file to the shared file service and returns its URL.
 * Same contract the profile KYC upload uses: multipart `file`, and a
 * { status: 201, data: [{ url }] } response. No auth required, which is
 * what lets the public account-opening wizard upload before the applicant
 * has a platform account.
 */
export const uploadToFileService = async (
  file: File,
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await fetch(`${uploadFileUrl}/files`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('upload-failed');
  }

  const result = await response.json();
  const url = result?.data?.[0]?.url;

  if (result?.status !== 201 || !url) {
    throw new Error(result?.message || 'upload-failed');
  }

  return url as string;
};

/** Lower-case extension of a file name, e.g. "jpg". */
export const fileExtension = (fileName: string, fallback = 'jpg') =>
  fileName.includes('.')
    ? fileName.split('.').pop()?.toLowerCase() || fallback
    : fallback;

/**
 * Body of the platform's UploadKYCDocs call. The platform fails with
 * "2016 Upload Failed" without `url` and `FileType`/`Extension`: it fetches
 * the file from the file service and needs the type to store it (the
 * file-service URL itself carries no extension). Same shape web-app-v2
 * sends.
 */
export const buildKycUploadPayload = ({
  PID,
  url,
  type,
  extension,
}: {
  PID: string | null | undefined;
  url: string;
  type: string;
  extension: string;
}) => ({
  PID,
  MediaSourceURL: url,
  url,
  Type: type,
  FileType: extension,
  Extension: extension,
});
